/**
 * Pages Service - Lecture des pages éditoriales publiées
 *
 * Les pages de contenu vivent en base et se lisent par leur slug et leur
 * langue. Une page absente dans la langue demandée est servie en français :
 * l'API signale ce repli, et le service le remonte au composant pour qu'il
 * puisse en informer le visiteur.
 *
 * Le corps d'une page est un tableau de blocs typés, pas du HTML de mise en
 * page : chaque bloc est rendu par un composant React dédié.
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be';

/**
 * Lit le corps d'une page : un tableau de blocs typés, stocké en JSON.
 *
 * L'API peut le renvoyer déjà désérialisé (tableau) ou sous forme de chaîne
 * JSON, selon la sérialisation ColdFusion. Un corps illisible ou d'une forme
 * inattendue rend un tableau vide plutôt que de faire échouer la page : mieux
 * vaut une page sans contenu qu'un écran blanc.
 *
 * @param {string|Array} bodyJson
 * @returns {Array} Tableau de blocs, vide si le corps est absent ou illisible
 */
const parseBlocs = (bodyJson) => {
    if (Array.isArray(bodyJson)) {
        return bodyJson;
    }

    if (typeof bodyJson !== 'string' || !bodyJson.trim()) {
        return [];
    }

    try {
        const parsed = JSON.parse(bodyJson);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        console.error('Corps de page illisible, la page sera rendue sans contenu.');
        return [];
    }
};

/**
 * Normalise la réponse d'une lecture de page.
 *
 * L'API ColdFusion renvoie ses clés en MAJUSCULES (SUCCESS, DATA, MESSAGE),
 * mais pas systématiquement : selon la sérialisation, la même clé peut
 * arriver dans l'une ou l'autre casse. On lit donc les deux à chaque fois.
 *
 * @param {object} result - Corps JSON de la réponse
 * @returns {{found: boolean, page: object|null, isFallback: boolean, servedLang: string, requestedLang: string, message: string}}
 */
const normalizePageResponse = (result) => {
    const success = result?.success ?? result?.SUCCESS ?? false;
    const data = result?.data ?? result?.DATA ?? null;
    const message = result?.message ?? result?.MESSAGE ?? '';
    const isFallback = result?.isFallback ?? result?.ISFALLBACK ?? false;
    const servedLang = result?.servedLang ?? result?.SERVEDLANG ?? '';
    const requestedLang = result?.requestedLang ?? result?.REQUESTEDLANG ?? '';

    if (!success || !data) {
        return { found: false, page: null, isFallback: false, servedLang: '', requestedLang, message };
    }

    // Les colonnes de la page subissent la même incertitude de casse.
    // TROIS CASSES, PAS DEUX.
    //
    // queryToArray (cabaza.cfc, lCase sur chaque colonne) passe tous les noms
    // en MINUSCULES : l'API rend « bodyjson », « metadescription »,
    // « pageuuid »… que ni la forme camelCase ni la forme MAJUSCULE
    // n'attrapaient.
    //
    // CE QUE CELA CASSAIT, EN SILENCE. `blocs` retombait sur la chaîne vide :
    // la page s'affichait avec son titre et un corps VIDE, sans une erreur en
    // console. Cinq autres champs subissaient le même sort sans qu'on le
    // remarque, faute d'être affichés.
    const lire = (...noms) => {
        for (const nom of noms) {
            if (data[nom] !== undefined && data[nom] !== null) return data[nom];
        }
        return '';
    };

    const page = {
        pageUuid: lire('pageUuid', 'pageuuid', 'PAGEUUID'),
        slug: lire('slug', 'SLUG'),
        lang: lire('lang', 'LANG'),
        title: lire('title', 'TITLE'),
        metaDescription: lire('metaDescription', 'metadescription', 'METADESCRIPTION'),
        blocs: parseBlocs(lire('bodyJson', 'bodyjson', 'BODYJSON')),
        moduleCode: lire('moduleCode', 'modulecode', 'MODULECODE'),
        status: lire('status', 'STATUS'),
        publishedAt: lire('publishedAt', 'publishedat', 'PUBLISHEDAT'),
        updatedAt: lire('updatedAt', 'updatedat', 'UPDATEDAT')
    };

    return {
        found: true,
        page,
        // Le repli n'est pas booléen à coup sûr : SQL Server et la
        // sérialisation CF peuvent le rendre en 1/0 ou en 'true'/'false'.
        isFallback: isFallback === true || isFallback === 1 || String(isFallback).toLowerCase() === 'true',
        servedLang,
        requestedLang,
        message
    };
};

const NOT_FOUND = (message = '') => ({
    found: false,
    page: null,
    isFallback: false,
    servedLang: '',
    requestedLang: '',
    message
});

/**
 * Quelle page est publiée sur cette adresse ?
 *
 * C'est l'interrupteur du basculement : une page publiée sur `/install`
 * remplace la page d'origine ; dépubliée, l'ancienne reprend la main.
 *
 * Rend directement la page ou `null` — l'appelant n'a qu'une question à se
 * poser : « ai-je une page à afficher ? ». Un 404 n'est pas une erreur ici,
 * c'est la réponse normale quand rien n'est publié sur cette route.
 *
 * @param {string} route - L'adresse publique ('/install')
 * @param {string} lang - Code langue demandé
 * @returns {Promise<object|null>} La page, ou null pour garder le repli
 */
export const getPageByRoute = async (route, lang = 'fr') => {
    if (!route) return null;

    try {
        const url = `${API_BASE}/rest/cabaza/pages/par-route`
            + `?route=${encodeURIComponent(route)}&lang=${encodeURIComponent(lang)}`;
        const response = await fetch(url);

        if (response.status === 404) return null;
        if (!response.ok) return null;

        const result = await response.json();
        const normalise = normalizePageResponse(result);

        return normalise.found ? normalise.page : null;
    } catch (error) {
        // Une API injoignable ne doit pas produire une page blanche : on
        // garde la page d'origine.
        console.error('getPageByRoute:', error);
        return null;
    }
};

/**
 * Lit une page publiée par son slug, avec repli sur le français.
 *
 * @param {string} slug - Identifiant d'adresse de la page ('covoiturage')
 * @param {string} lang - Code langue demandé
 * @returns {Promise<{found: boolean, page: object|null, isFallback: boolean, servedLang: string, requestedLang: string, message: string}>}
 */
export const getPageBySlug = async (slug, lang = 'fr') => {
    if (!slug) {
        return NOT_FOUND('Slug manquant.');
    }

    try {
        const url = `${API_BASE}/rest/cabaza/pages/${encodeURIComponent(slug)}?lang=${encodeURIComponent(lang)}`;
        const response = await fetch(url);

        // Une page absente ou en brouillon donne un 404 : ce n'est pas une
        // panne, c'est une réponse. On la distingue d'une erreur réseau.
        if (response.status === 404) {
            return NOT_FOUND('Page introuvable.');
        }

        const result = await response.json();
        return normalizePageResponse(result);
    } catch (error) {
        console.error('Error fetching page:', error);
        return NOT_FOUND(error.message);
    }
};

/**
 * Lit la page décrivant un module, avec repli sur le français.
 *
 * @param {string} moduleCode - Code du module ('CARPOOLING')
 * @param {string} lang - Code langue demandé
 * @returns {Promise<{found: boolean, page: object|null, isFallback: boolean, servedLang: string, requestedLang: string, message: string}>}
 */
export const getPageByModuleCode = async (moduleCode, lang = 'fr') => {
    if (!moduleCode) {
        return NOT_FOUND('Code de module manquant.');
    }

    try {
        const url = `${API_BASE}/rest/cabaza/pages/module/${encodeURIComponent(moduleCode)}?lang=${encodeURIComponent(lang)}`;
        const response = await fetch(url);

        if (response.status === 404) {
            return NOT_FOUND('Page introuvable.');
        }

        const result = await response.json();
        return normalizePageResponse(result);
    } catch (error) {
        console.error('Error fetching module page:', error);
        return NOT_FOUND(error.message);
    }
};
